import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Platform, Text, TouchableOpacity } from 'react-native';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';

export interface LiveVisitMapPosition {
  latitude: number;
  longitude: number;
}

export type LiveVisitTravelMode = 'driving' | 'walking' | 'cycling' | 'motorcycle';

const TRAVEL_MODE_GLYPH: Record<LiveVisitTravelMode, string> = {
  driving: '🚗',
  motorcycle: '🏍️',
  cycling: '🚲',
  walking: '🚶',
};

/** One candidate route the agent could take — mirrors the backend's
 * RouteAlternative/LiveDirections primary-route fields. `index` 0 is always
 * the primary/currently-selected route by convention on the mobile side;
 * alternatives from the backend are appended after it. */
export interface LiveVisitRouteOption {
  distanceMeters: number;
  durationSeconds?: number;
  geometry: Array<[number, number]>;
}

interface LiveVisitMapProps {
  agentPosition: LiveVisitMapPosition | null;
  destination: LiveVisitMapPosition & { address?: string };
  /** [latitude, longitude] pairs for the FULL route, captured ONCE at the
   * start of the in-progress phase and held static afterward (see
   * VisitExecuteScreen's originalRouteGeometry) — NOT the backend's live,
   * continually-re-trimmed "remaining route" response. This component
   * finds the point on this static route nearest the current
   * agentPosition on every update and splits the line there: grey behind
   * the agent (already traveled), blue ahead (remaining) — the standard
   * navigation-app progress-line look. A straight two-point line when
   * OSRM wasn't available server-side still works the same way, just
   * with only one segment to split. */
  geometry: Array<[number, number]>;
  /** Other candidate routes for the SAME origin/destination, drawn as
   * selectable muted lines alongside the primary one — undefined/empty when
   * the engine offered no alternatives (haversine, or OSRM found only one
   * reasonable path). Tapping one (on the map or in the ETA list) calls
   * onSelectAlternative with its index into THIS array (not into a combined
   * [primary, ...alternatives] list), letting the caller swap its own
   * geometry/baseline state to match. */
  alternativeRoutes?: LiveVisitRouteOption[];
  onSelectAlternative?: (index: number) => void;
  /** Which vehicle glyph to render for the moving agent marker — the turn
   * banner that used to live on top of the map was removed in favor of a
   * dedicated next-step panel outside the map (see VisitExecuteScreen), so
   * this component now only owns the map surface itself. */
  travelMode: LiveVisitTravelMode;
  height?: number;
  /** Renders the map filling its container edge-to-edge with an overlaid
   * close button, for the fullscreen takeover mode — VisitExecuteScreen
   * swaps height/positioning around this component rather than this
   * component managing its own portal/modal, keeping it a plain view. */
  isFullscreen?: boolean;
  /** Dismisses the directions view without ending the visit. Rendered inside
   * the map's own layer so native WebViews cannot cover a sibling control. */
  onClose?: () => void;
  onToggleFullscreen?: () => void;
}

/**
 * Embedded live navigation map — Talabat/Uber-style directions view shown
 * during an in-progress visit. Built on Leaflet-in-WebView (the SAME
 * approach MapLocationPicker.tsx already uses for its location picker),
 * deliberately NOT react-native-maps: that library isn't installed in this
 * project and requires a native rebuild (incompatible with Expo Go), while
 * this WebView approach works immediately on both native and web with the
 * dependency (react-native-webview) already present.
 *
 * Renders a moving marker (a vehicle-type emoji matching the selected travel
 * mode) for the agent's live position (updated via postMessage as
 * VisitExecuteScreen's location watch fires, without reloading the whole
 * WebView/map), a fixed marker for the visit's target, and a polyline
 * connecting them using whatever geometry was resolved server-side
 * (road-following via OSRM when configured, straight-line fallback
 * otherwise).
 */
export const LiveVisitMap: React.FC<LiveVisitMapProps> = ({
  agentPosition,
  destination,
  geometry,
  alternativeRoutes,
  onSelectAlternative,
  travelMode,
  height = 260,
  isFullscreen = false,
  onClose,
  onToggleFullscreen,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const webViewRef = useRef<WebView>(null);
  // Web: this instance's own iframe. A screen can show two maps at once (the
  // visit details card + the fullscreen directions map), so looking the
  // iframe up by a fixed id sent every live update to the wrong one.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  // Only meaningfully non-zero when isFullscreen (the WebView fills the
  // real device screen then, notch/Dynamic Island/home-indicator included);
  // in the normal card-embedded mode this component sits inside whatever
  // padding its parent screen already provides, so the buttons stay at
  // their plain spacing.sm offsets there.
  const insets = useSafeAreaInsets();

  const [isOffCenter, setIsOffCenter] = useState(false);

  // Confirmed real UX gap: the device could lock/dim mid-navigation while
  // this fullscreen map was the active screen, which is exactly when the
  // agent most needs to glance at it hands-free while driving. Scoped to
  // isFullscreen specifically (not every LiveVisitMap render — the small
  // embedded desktop-web panel doesn't warrant keeping the screen awake) —
  // activates while this instance is mounted fullscreen, deactivates on
  // prop change or unmount, same as expo-camera's own capture-screen
  // pattern elsewhere in this app.
  useEffect(() => {
    if (!isFullscreen) return;
    const tag = 'live-visit-map-fullscreen';
    activateKeepAwakeAsync(tag);
    return () => {
      deactivateKeepAwake(tag);
    };
  }, [isFullscreen]);

  const postToMap = (message: object) => {
    const payload = JSON.stringify(message);
    if (Platform.OS === 'web') {
      iframeRef.current?.contentWindow?.postMessage(payload, '*');
    } else {
      webViewRef.current?.postMessage(payload);
    }
  };

  // Push live position/geometry updates into the already-loaded map instead
  // of reloading the HTML each time — reloading would flash/reset the map's
  // pan+zoom state on every GPS tick.
  useEffect(() => {
    if (!isMapReady) return;
    postToMap({
      type: 'update',
      agentPosition,
      destination,
      geometry,
      alternativeRoutes: alternativeRoutes ?? [],
      travelModeGlyph: TRAVEL_MODE_GLYPH[travelMode],
    });
  }, [isMapReady, agentPosition, destination, geometry, alternativeRoutes, travelMode]);

  const recenter = () => {
    postToMap({ type: 'recenter' });
    setIsOffCenter(false);
  };

  // Leaflet sizes its canvas from the container's dimensions at creation
  // time — when the fullscreen toggle changes this component's own size
  // (via VisitExecuteScreen swapping layout around it) without remounting
  // the WebView, Leaflet's internal size cache goes stale and the map
  // renders cropped/blank until a manual nudge, hence invalidateSize().
  useEffect(() => {
    if (!isMapReady) return;
    postToMap({ type: 'invalidateSize' });
  }, [isMapReady, isFullscreen]);

  const mapHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <style>
        body { margin: 0; padding: 0; }
        #map { height: 100vh; width: 100vw; }
        .agent-marker {
          width: 28px;
          height: 28px;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.45));
        }
        .destination-marker {
          background: #EF4444;
          border: 3px solid white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.35);
        }
      </style>
    </head>
    <body>
      <div id="map"></div>
      <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
      <script>
        const destination = ${JSON.stringify(destination)};

        const map = L.map('map', { zoomControl: false }).setView([destination.latitude, destination.longitude], 14);
        L.control.zoom({ position: 'bottomright' }).addTo(map);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        // Confirmed real bug: alternative routes and the primary
        // traveled/remaining lines were all added to Leaflet's default
        // overlay pane, where z-order is purely "last added wins". The
        // primary route redraws itself (removeLayer + addTo) on EVERY GPS
        // tick (redrawRouteSplit, a few seconds apart) — each redraw pushed
        // it back on top of the stack, permanently burying the alternative
        // routes' click layer within moments of the trip starting, well
        // before the agent ever had a chance to tap one. Dedicated panes
        // with an explicit z-index fix this once and for all: alternatives
        // always render in a pane below the primary route's pane, so the
        // primary redrawing itself never re-covers them again — no need to
        // re-raise the alternatives after every tick.
        map.createPane('alternativeRoutesPane').style.zIndex = 410;
        map.createPane('primaryRoutePane').style.zIndex = 420;

        const destinationMarker = L.marker([destination.latitude, destination.longitude], {
          icon: L.divIcon({ className: 'destination-marker', html: '<div></div>', iconSize: [18, 18] }),
        }).addTo(map);

        let agentMarker = null;
        let traveledLine = null;
        let remainingLine = null;
        let fullRoute = null;
        // Same key-comparison approach as lastAlternativesKey below — lets
        // fullRoute be replaced when VisitExecuteScreen sends a genuinely
        // NEW baseline (switching routes, or a reroute after the agent goes
        // off the original path), while still avoiding pointless reassigns
        // when the same route arrives again unchanged.
        let lastFullRouteKey = null;
        let alternativeLines = [];
        // JSON.stringify comparison of the raw geometry arrays — cheap
        // enough at the size of a route's point list, and avoids redrawing
        // (and losing any in-progress tap feedback on) the alternative
        // lines on every single GPS tick when the alternatives themselves
        // haven't actually changed since the last update.
        let lastAlternativesKey = null;
        let hasCenteredOnAgent = false;
        // Close, navigation-style zoom on the agent's own position —
        // deliberately NOT fitBounds-ing the whole route (that zooms OUT
        // to show start-to-finish, which is the opposite of what a
        // turn-by-turn driving view wants): the agent needs to see the
        // road immediately around them, not the entire trip at a glance.
        const AGENT_ZOOM = 17;

        // A single navigation-puck arrow (not a per-travel-mode emoji) — a
        // filled blue circle with a triangular pointer, the same convention
        // Google Maps/Waze use for the live position marker. travelModeGlyph
        // is now unused for the marker itself — kept flowing through the
        // update message only because other code paths (postToMap's payload
        // shape) still reference it.
        // The pointer's SVG content lives in its own <g> so it can be
        // rotated independently of the outer <svg>/divIcon wrapper — Leaflet
        // positions the divIcon's own root element with its own CSS
        // transform (translate to the marker's lat/lng), so rotating that
        // same element directly would fight Leaflet's positioning instead
        // of composing with it. Rotating this inner group avoids that
        // entirely: whatever Leaflet does to the wrapper, the group's own
        // transform is untouched by it.
        const AGENT_ICON_HTML =
          '<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">' +
          '<g id="agent-marker-rotor" style="transform-origin: 14px 14px;">' +
          '<circle cx="14" cy="14" r="12" fill="#0EA5E9" stroke="#FFFFFF" stroke-width="2.5"/>' +
          '<path d="M14 6 L19 17 L14 14 L9 17 Z" fill="#FFFFFF"/>' +
          '</g>' +
          '</svg>';

        function agentIcon() {
          return L.divIcon({ className: 'agent-marker', html: AGENT_ICON_HTML, iconSize: [28, 28] });
        }

        // Direction the agent last genuinely moved, in degrees (0 = north,
        // clockwise) — null until the second GPS fix arrives (a single
        // point alone has no direction). The map itself stays north-up
        // (see this file's own removal of the earlier whole-map heading-up
        // rotation attempt); only this one small arrow rotates in place.
        let lastBearingDeg = null;

        function applyAgentMarkerRotation() {
          if (lastBearingDeg === null || !agentMarker) return;
          const el = agentMarker.getElement();
          const rotor = el && el.querySelector('#agent-marker-rotor');
          if (rotor) rotor.style.transform = 'rotate(' + lastBearingDeg + 'deg)';
        }

        // Bearing (degrees, 0 = north, clockwise) from point a to point b —
        // used to rotate ONLY the agent marker's own icon to face the
        // direction of travel, distinct from (and much simpler than) the
        // abandoned whole-map heading-up rotation: the map itself always
        // stays north-up here, just the little arrow spins in place, the
        // same way a plain "you are here" dot with a direction cone works
        // on a north-up map.
        function bearingBetween(a, b) {
          const toRad = function (d) { return (d * Math.PI) / 180; };
          const toDeg = function (r) { return (r * 180) / Math.PI; };
          const lat1 = toRad(a[0]);
          const lat2 = toRad(b[0]);
          const dLon = toRad(b[1] - a[1]);
          const y = Math.sin(dLon) * Math.cos(lat2);
          const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
          return (toDeg(Math.atan2(y, x)) + 360) % 360;
        }

        // Great-circle-ish squared distance in degree-space — fine for
        // finding the NEAREST of many closely-spaced route points (a few
        // hundred meters apart at most), where the small distortion from
        // not doing real haversine math never changes which point wins.
        function sqDist(a, b) {
          const dLat = a[0] - b[0];
          const dLon = a[1] - b[1];
          return dLat * dLat + dLon * dLon;
        }

        // Splits the static full route into "already traveled" (grey,
        // behind the agent) and "remaining" (blue, ahead) segments by
        // finding the closest point on the route to the agent's current
        // position — the standard navigation-app progress-line look.
        // Confirmed real gap: the backend's own live-directions response
        // already re-trims to remaining-only server-side, which is enough
        // to keep the ROUTE ACCURATE but throws away the traveled segment
        // entirely, leaving nothing to greatly out — this recomputes both
        // halves from the one static route captured at visit-start instead.
        function redrawRouteSplit(agentPos) {
          if (!Array.isArray(fullRoute) || fullRoute.length < 2) return;

          let nearestIndex = 0;
          let nearestDist = Infinity;
          for (let i = 0; i < fullRoute.length; i++) {
            const d = sqDist(fullRoute[i], agentPos);
            if (d < nearestDist) {
              nearestDist = d;
              nearestIndex = i;
            }
          }

          const traveledPoints = fullRoute.slice(0, nearestIndex + 1);
          const remainingPoints = fullRoute.slice(nearestIndex);

          if (traveledLine) map.removeLayer(traveledLine);
          if (remainingLine) map.removeLayer(remainingLine);
          if (traveledPoints.length > 1) {
            traveledLine = L.polyline(traveledPoints, { color: '#9CA3AF', weight: 4, opacity: 0.7, pane: 'primaryRoutePane' }).addTo(map);
          }
          if (remainingPoints.length > 1) {
            remainingLine = L.polyline(remainingPoints, { color: '#0EA5E9', weight: 5, opacity: 0.9, pane: 'primaryRoutePane' }).addTo(map);
          }
        }

        // Draws each alternative route as a muted, dashed grey line — kept
        // visually secondary to the primary traveled/remaining lines above
        // (which stay blue/grey-solid), and each one is independently
        // tappable to switch to it. Redrawn wholesale on every genuinely new
        // alternatives list rather than diffed/updated in place — there are
        // at most a handful of these (OSRM realistically offers 1-3), so the
        // simplicity of a full clear+redraw isn't a meaningful cost.
        function redrawAlternatives(routes) {
          alternativeLines.forEach(function (line) { map.removeLayer(line); });
          alternativeLines = [];
          if (!Array.isArray(routes)) return;
          routes.forEach(function (route, index) {
            if (!Array.isArray(route.geometry) || route.geometry.length < 2) return;

            function selectThisRoute() {
              const message = JSON.stringify({ type: 'alternativeSelected', index: index });
              if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(message);
              else if (window.parent) window.parent.postMessage(message, '*');
            }

            // Confirmed real bug: a thin (weight:4) dashed line is a very
            // small, unreliable tap target on a touch screen — Leaflet only
            // registers a click within the line's own rendered stroke.
            // Standard Leaflet workaround: draw an invisible, much wider
            // line UNDERNEATH the visible one, purely to catch taps — the
            // visible line keeps its actual thin/dashed styling, the hit
            // area is just far more forgiving of an imprecise tap.
            const hitArea = L.polyline(route.geometry, {
              color: '#000000',
              weight: 28,
              opacity: 0,
              pane: 'alternativeRoutesPane',
            }).addTo(map);
            hitArea.on('click', selectThisRoute);
            alternativeLines.push(hitArea);

            // Same blue as the primary/remaining route, just lighter — an
            // alternative is a fully viable route the agent could switch
            // to, not a lesser/muted option, so it keeps the primary
            // route's own color rather than a desaturated grey.
            const line = L.polyline(route.geometry, {
              color: '#0EA5E9',
              weight: 4,
              opacity: 0.4,
              pane: 'alternativeRoutesPane',
              interactive: false,
            }).addTo(map);
            alternativeLines.push(line);
          });
        }

        let lastAgentPos = null;
        // True once the map has ever centered on the agent — after that
        // point, further agentPosition updates only pan (not re-zoom) if
        // the user hasn't manually moved the map away (see userPannedAway
        // below), so a live GPS tick doesn't fight a deliberate
        // pinch-zoom/pan the agent just made to look around.
        let userPannedAway = false;

        function applyUpdate(data) {
          // VisitExecuteScreen holds this fixed as a baseline between real
          // rebaselines (visit start, switching routes, or a reroute after
          // the agent drifts off the original path) — comparing a key
          // rather than gating on "only the very first ever" lets a genuine
          // rebaseline actually replace fullRoute instead of being silently
          // ignored forever after the first one arrived.
          if (Array.isArray(data.geometry) && data.geometry.length > 1) {
            const routeKey = JSON.stringify(data.geometry);
            if (routeKey !== lastFullRouteKey) {
              lastFullRouteKey = routeKey;
              fullRoute = data.geometry;
            }
          }

          if (Array.isArray(data.alternativeRoutes)) {
            const key = JSON.stringify(data.alternativeRoutes.map(function (r) { return r.geometry; }));
            if (key !== lastAlternativesKey) {
              lastAlternativesKey = key;
              redrawAlternatives(data.alternativeRoutes);
            }
          }

          if (data.agentPosition) {
            const pos = [data.agentPosition.latitude, data.agentPosition.longitude];

            // Computed BEFORE lastAgentPos is overwritten below — needs the
            // previous tick's position to know which way the agent just
            // moved. Skipped for a near-zero-distance move (GPS jitter
            // while stationary) so the arrow doesn't spin randomly at a red
            // light; keeps pointing whichever way it last genuinely moved.
            if (lastAgentPos && sqDist(lastAgentPos, pos) > 1e-10) {
              lastBearingDeg = bearingBetween(lastAgentPos, pos);
            }
            lastAgentPos = pos;
            if (agentMarker) {
              agentMarker.setLatLng(pos);
            } else {
              agentMarker = L.marker(pos, { icon: agentIcon() }).addTo(map);
            }
            applyAgentMarkerRotation();
            if (!hasCenteredOnAgent) {
              map.setView(pos, AGENT_ZOOM);
              hasCenteredOnAgent = true;
            } else if (!userPannedAway) {
              map.panTo(pos, { animate: true });
            }
            redrawRouteSplit(pos);
          }
        }

        function notifyOffCenter(offCenter) {
          const message = JSON.stringify({ type: 'offCenter', value: offCenter });
          if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(message);
          else if (window.parent) window.parent.postMessage(message, '*');
        }

        // A real user drag (not the programmatic panTo above, which never
        // fires 'dragstart') means the agent deliberately moved the map to
        // look at something else — stop auto-following until they tap the
        // recenter control, mirroring how Google/Uber-style navigation maps
        // behave once you pan away from your own position.
        map.on('dragstart', function () {
          userPannedAway = true;
          notifyOffCenter(true);
        });

        function postReady() {
          const message = JSON.stringify({ type: 'ready' });
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(message);
          } else if (window.parent) {
            window.parent.postMessage(message, '*');
          }
        }

        function handleIncoming(data) {
          if (data.type === 'invalidateSize') {
            map.invalidateSize();
            // Re-centers on the agent at the same close zoom rather than
            // fitBounds-ing the whole route — a container resize (e.g.
            // toggling fullscreen) shouldn't zoom back out to trip-overview
            // level, it should keep the same tight driving-view framing.
            if (lastAgentPos) map.setView(lastAgentPos, AGENT_ZOOM);
            return;
          }
          if (data.type === 'recenter') {
            userPannedAway = false;
            // Restores the tight driving-view zoom too, not just position —
            // "recenter" resuming live tracking should put the agent back
            // in the same close framing they started with, not just pan to
            // wherever they are at whatever zoom level was left over from
            // manual pinch-zooming.
            if (lastAgentPos) map.setView(lastAgentPos, AGENT_ZOOM, { animate: true });
            notifyOffCenter(false);
            return;
          }
          applyUpdate(data);
        }

        // Native WebView delivers messages via a 'message' event on document;
        // the web/iframe embedding (see LiveVisitMap.tsx's Platform.OS==='web'
        // branch) posts via window.postMessage instead — both routed through
        // the same handleIncoming, mirroring MapLocationPicker.tsx's identical
        // dual-path bridge.
        document.addEventListener('message', function (event) {
          try { handleIncoming(JSON.parse(event.data)); } catch (e) { console.error('[LiveVisitMap iframe] document message error', e, event.data); }
        });
        window.addEventListener('message', function (event) {
          try { handleIncoming(JSON.parse(event.data)); } catch (e) { console.error('[LiveVisitMap iframe] window message error', e, event.data); }
        });

        postReady();
      </script>
    </body>
    </html>
  `;

  const handleIncomingBridgeMessage = (raw: string) => {
    try {
      const data = JSON.parse(raw);
      if (data.type === 'ready') setIsMapReady(true);
      else if (data.type === 'offCenter') setIsOffCenter(!!data.value);
      else if (data.type === 'alternativeSelected' && typeof data.index === 'number') {
        onSelectAlternative?.(data.index);
      }
    } catch {
      // ignore malformed messages
    }
  };

  const handleMessage = (event: any) => handleIncomingBridgeMessage(event.nativeEvent.data);

  // Web: the iframe posts back to window.parent (see postReady/notifyOffCenter
  // in the injected script) — needs a listener on the actual browser window,
  // separate from the native WebView's onMessage prop above. Confirmed real
  // gap: only 'ready' was ever wired (via iframe's own onLoad, which fires
  // independently of this bridge), so 'offCenter' had no way to reach
  // React state on web at all before this.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const listener = (event: MessageEvent) => {
      // Only this instance's iframe (another map on the screen posts too)
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (typeof event.data === 'string') handleIncomingBridgeMessage(event.data);
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  // Fullscreen-only inset offset — keeps every overlay button clear of the
  // notch/Dynamic Island (top) the same way mobileTopOverlay/mobileSheet in
  // VisitExecuteScreen need to clear the notch/home-indicator. Zero in the
  // normal card-embedded mode, where this component already sits inside a
  // parent screen's own safe-area-aware padding.
  const topInset = isFullscreen ? insets.top : 0;

  const fullscreenAction = isFullscreen ? onClose || onToggleFullscreen : onToggleFullscreen;
  const fullscreenButton = fullscreenAction ? (
    <TouchableOpacity
      style={[styles.fullscreenButton, isFullscreen && styles.closeButton, {
        top: spacing.md + topInset,
        right: spacing.md + (isFullscreen ? Math.max(insets.left, insets.right) : 0),
      }]}
      onPress={fullscreenAction}
      accessibilityRole="button"
      accessibilityLabel={t(isFullscreen ? 'visits.map.closeDirections' : 'visits.map.expand')}
      testID={isFullscreen ? 'visit-directions-close' : 'visit-map-expand'}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      <Ionicons name={isFullscreen ? 'close' : 'expand-outline'} size={22} color={colors.text.primary as string} />
      {isFullscreen ? <Text style={styles.closeText} numberOfLines={1}>{t('visits.map.closeDirections')}</Text> : null}
    </TouchableOpacity>
  ) : null;

  // Shown once the agent manually pans/zooms away from their own live
  // position (see the Leaflet 'dragstart' listener above) — tapping it
  // resumes auto-following instead of leaving the map stuck wherever
  // they last dragged it.
  const cameraControlsColumn = isOffCenter ? (
    <View style={[styles.cameraControlsColumn, { top: spacing.md + topInset + 60, right: spacing.md + (isFullscreen ? Math.max(insets.left, insets.right) : 0) }]}>
      <TouchableOpacity style={styles.recenterButton} onPress={recenter} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Ionicons name="locate" size={20} color={colors.white as string} />
      </TouchableOpacity>
    </View>
  ) : null;

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.container, isFullscreen ? styles.fullscreenContainer : { height }]}>
        <iframe
          ref={iframeRef}
          srcDoc={mapHTML}
          style={{ width: '100%', height: '100%', border: 'none' }}
          onLoad={() => setIsMapReady(true)}
        />
        {fullscreenButton}
        {cameraControlsColumn}
      </View>
    );
  }

  return (
    <View style={[styles.container, isFullscreen ? styles.fullscreenContainer : { height }]}>
      <WebView
        ref={webViewRef}
        source={{ html: mapHTML }}
        style={styles.map}
        onMessage={handleMessage}
        originWhitelist={['*']}
      />
      {!isMapReady && (
        <View style={styles.loadingOverlay}>
          <Text style={styles.loadingText}>Loading map…</Text>
        </View>
      )}
      {fullscreenButton}
      {cameraControlsColumn}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: { borderRadius: 12, overflow: 'hidden', backgroundColor: colors.background.secondary as string },
  fullscreenContainer: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 0,
    zIndex: 1000,
  },
  map: { flex: 1, backgroundColor: 'transparent' },
  loadingOverlay: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  loadingText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  fullscreenButton: {
    position: 'absolute',
    right: spacing.sm,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.background.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 2,
  },
  closeButton: {
    width: 'auto',
    minWidth: 48,
    maxWidth: 220,
    height: 48,
    borderRadius: 24,
    paddingHorizontal: spacing.sm,
    flexDirection: 'row',
    gap: spacing.xs,
  },
  closeText: { flexShrink: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  // Positioned below the fullscreen button rather than sharing its row —
  // avoids the two controls visually competing for the same corner.
  cameraControlsColumn: {
    position: 'absolute',
    right: spacing.sm,
    alignItems: 'center',
    gap: spacing.sm,
  },
  recenterButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
});
